import { Link } from "@tanstack/react-router";
import {
  BookOpen,
  Briefcase,
  Cpu,
  FlaskConical,
  Globe2,
  Lightbulb,
  Settings as SettingsIcon,
  Target,
  Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { useMe } from "@/queries/auth";
import { cn } from "@/lib/utils";

const items = [
  { to: "/world", icon: Globe2, labelKey: "leftNav.world" },
  { to: "/hypotheses", icon: Lightbulb, labelKey: "leftNav.hypotheses" },
  { to: "/portfolio", icon: Briefcase, labelKey: "leftNav.portfolio" },
  { to: "/lab", icon: FlaskConical, labelKey: "leftNav.lab" },
] as const;

export function LeftNav() {
  const { t } = useTranslation("common");
  const { t: tAdmin } = useTranslation("admin-users");
  const { t: tSettings } = useTranslation("admin-settings");
  const { t: tAgents } = useTranslation("admin-agents");
  const { t: tPlaybooks } = useTranslation("admin-playbooks");
  const { t: tStrategies } = useTranslation("admin-strategies");
  const { data: me } = useMe();
  const isAdmin = me?.role === "admin";

  return (
    <nav className="flex w-56 flex-col border-r bg-card p-3 gap-1">
      {items.map(({ to, icon: Icon, labelKey }) => (
        <Link
          key={to}
          to={to}
          className={cn(
            "flex items-center gap-2 rounded-md px-3 py-2 text-sm",
            "hover:bg-accent hover:text-accent-foreground",
          )}
          activeProps={{ className: "bg-accent text-accent-foreground" }}
        >
          <Icon className="h-4 w-4" />
          {t(labelKey)}
        </Link>
      ))}

      {isAdmin ? (
        <>
          <div className="mt-3 px-3 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {tAdmin("nav.section")}
          </div>
          <Link
            to="/admin/users"
            className={cn(
              "flex items-center gap-2 rounded-md px-3 py-2 text-sm",
              "hover:bg-accent hover:text-accent-foreground",
            )}
            activeProps={{ className: "bg-accent text-accent-foreground" }}
          >
            <Users className="h-4 w-4" />
            {tAdmin("nav.users")}
          </Link>
          <Link
            to="/admin/agents"
            className={cn(
              "flex items-center gap-2 rounded-md px-3 py-2 text-sm",
              "hover:bg-accent hover:text-accent-foreground",
            )}
            activeProps={{ className: "bg-accent text-accent-foreground" }}
          >
            <Cpu className="h-4 w-4" />
            {tAgents("nav.agents")}
          </Link>
          <Link
            to="/admin/playbooks"
            className={cn(
              "flex items-center gap-2 rounded-md px-3 py-2 text-sm",
              "hover:bg-accent hover:text-accent-foreground",
            )}
            activeProps={{ className: "bg-accent text-accent-foreground" }}
          >
            <BookOpen className="h-4 w-4" />
            {tPlaybooks("nav.playbooks")}
          </Link>
          <Link
            to="/admin/strategies"
            className={cn(
              "flex items-center gap-2 rounded-md px-3 py-2 text-sm",
              "hover:bg-accent hover:text-accent-foreground",
            )}
            activeProps={{ className: "bg-accent text-accent-foreground" }}
          >
            <Target className="h-4 w-4" />
            {tStrategies("nav.strategies")}
          </Link>
          <Link
            to="/admin/settings"
            className={cn(
              "flex items-center gap-2 rounded-md px-3 py-2 text-sm",
              "hover:bg-accent hover:text-accent-foreground",
            )}
            activeProps={{ className: "bg-accent text-accent-foreground" }}
          >
            <SettingsIcon className="h-4 w-4" />
            {tSettings("nav.settings")}
          </Link>
        </>
      ) : null}
    </nav>
  );
}
